import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  X,
  Download,
  Upload,
  Shield,
  Lock,
  Eye,
  FileText,
  Calendar,
  Tag,
  AlertTriangle,
  Clock,
  User,
  Trash2,
  Sparkles,
} from "lucide-react";
import { AnimatePresence } from "framer-motion";
import { documentsApi } from "../../services/api";
import { notifyError } from "../ui/Toast";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import AiDocumentSummary from "./AiDocumentSummary";

interface DocumentVersion {
  _id: string;
  file: {
    filename: string;
    mimeType: string;
    sizeBytes: number;
    sha256: string;
  };
  storage: {
    provider: string;
    key: string;
  };
  source: string;
  createdAt: string;
  createdBy?: string;
}

interface DocumentData {
  _id: string;
  title: string;
  description?: string;
  type: string;
  tags?: string[];
  classification?: {
    isPrivileged?: boolean;
    containsPii?: boolean;
    containsFinancial?: boolean;
    sensitivity?: string;
  };
  security?: {
    visibility?: string;
  };
  retention?: {
    legalHold?: boolean;
    retainUntil?: string;
  };
  currentVersionId?: DocumentVersion | null;
  versions?: DocumentVersion[];
  matterId?: { _id: string; title?: string } | string | null;
  clientId?: { _id: string; name?: string } | string | null;
  createdAt: string;
  createdBy?: string;
}

const typeLabels: Record<string, string> = {
  intake_upload: "Intake Upload",
  retainer: "Retainer",
  correspondence: "Correspondence",
  pleading: "Pleading",
  evidence: "Evidence",
  invoice_pdf: "Invoice PDF",
  receipt: "Receipt",
  closing_statement: "Closing Statement",
  title_search: "Title Search",
  mortgage_instructions: "Mortgage Instructions",
  other: "Other",
};

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

interface Props {
  documentId: string;
  onClose: () => void;
  onRefresh: () => void;
}

const DocumentDetail: React.FC<Props> = ({ documentId, onClose, onRefresh }) => {
  const [doc, setDoc] = useState<DocumentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showAiSummary, setShowAiSummary] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fetchDoc = useCallback(async () => {
    setLoading(true);
    try {
      const res = await documentsApi.get(documentId);
      setDoc(res.data.data);
    } catch (err) {
      console.error("Error fetching document:", err);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchDoc();
  }, [fetchDoc]);

  const handleTogglePrivilege = async () => {
    if (!doc) return;
    try {
      await documentsApi.updateClassification(doc._id, {
        isPrivileged: !doc.classification?.isPrivileged,
      });
      fetchDoc();
    } catch (err) {
      console.error("Error updating classification:", err);
    }
  };

  const handleTogglePii = async () => {
    if (!doc) return;
    try {
      await documentsApi.updateClassification(doc._id, {
        containsPii: !doc.classification?.containsPii,
      });
      fetchDoc();
    } catch (err) {
      console.error("Error updating PII flag:", err);
    }
  };

  const handleSensitivity = async (sensitivity: string) => {
    if (!doc) return;
    try {
      await documentsApi.updateClassification(doc._id, { sensitivity });
      fetchDoc();
    } catch (err) {
      console.error("Error updating sensitivity:", err);
    }
  };

  const handleToggleLegalHold = async () => {
    if (!doc) return;
    try {
      await documentsApi.updateLegalHold(doc._id, !doc.retention?.legalHold);
      fetchDoc();
    } catch (err) {
      console.error("Error updating legal hold:", err);
    }
  };

  const handleUploadVersion = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!doc || !e.target.files?.[0]) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      await documentsApi.uploadVersion(doc._id, formData);
      fetchDoc();
      onRefresh();
    } catch (err) {
      console.error("Error uploading version:", err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    try {
      await documentsApi.remove(doc._id);
      onClose();
      onRefresh();
    } catch (err) {
      console.error("Error deleting document:", err);
      notifyError("Couldn't delete the document.", err);
      setConfirmDelete(false);
    }
  };

  const handleDownload = (version: DocumentVersion) => {
    const url = documentsApi.downloadUrl(version.storage.key);
    window.open(url, "_blank");
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex justify-end"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: 0 }}
        exit={{ x: "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="relative w-full max-w-xl bg-white shadow-xl overflow-y-auto"
      >
        {loading || !doc ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--color-primary)" }} />
          </div>
        ) : (
          <div className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1 min-w-0 pr-4">
                <h2 className="text-xl font-bold" style={{ color: "#1F2937" }}>
                  {doc.title}
                </h2>
                {doc.description && (
                  <p className="text-sm text-slate-500 mt-1">{doc.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                    {typeLabels[doc.type] || doc.type}
                  </span>
                  {doc.tags?.map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-brand-light text-brand rounded-full text-xs font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 mb-6">
              {doc.currentVersionId && (
                <button
                  onClick={() => handleDownload(doc.currentVersionId as DocumentVersion)}
                  className="flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors"
                  style={{ backgroundColor: "var(--color-primary)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--color-primary-hover)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--color-primary)"; }}
                >
                  <Download className="h-4 w-4" />
                  Download
                </button>
              )}
              <label
                className={`flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-sm font-medium rounded-xl cursor-pointer hover:bg-slate-50 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
              >
                <Upload className="h-4 w-4" />
                {uploading ? "Uploading..." : "New Version"}
                <input type="file" className="hidden" onChange={handleUploadVersion} disabled={uploading} />
              </label>
              <button
                onClick={() => setShowAiSummary(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-sm font-medium rounded-xl hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                style={{ color: "var(--color-primary)" }}
              >
                <Sparkles className="h-4 w-4" />
                AI Summary
              </button>
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-xl hover:bg-red-50 transition-colors ml-auto"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
              ) : (
                <div className="flex items-center gap-2 ml-auto">
                  <span className="text-xs text-red-600">Delete this document?</span>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded-xl hover:bg-red-700 transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>

            {/* Classification Section */}
            <section className="bg-slate-50 rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Shield className="h-4 w-4" />
                Classification
              </h3>
              <div className="space-y-3">
                {/* Privilege toggle */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Attorney-Client Privilege</span>
                  <button
                    onClick={handleTogglePrivilege}
                    className={`relative w-10 h-5 rounded-full transition-colors ${doc.classification?.isPrivileged ? "bg-emerald-500" : "bg-slate-300"}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${doc.classification?.isPrivileged ? "translate-x-5" : ""}`}
                    />
                  </button>
                </div>

                {/* Sensitivity dropdown */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Sensitivity</span>
                  <div className="w-48">
                    <SearchableSelect
                      options={[
                        { value: "normal", label: "Normal" },
                        { value: "confidential", label: "Confidential" },
                        { value: "highly_confidential", label: "Highly Confidential" },
                      ]}
                      value={doc.classification?.sensitivity || "normal"}
                      onChange={(val) => { if (val) handleSensitivity(val); }}
                      placeholder="Select sensitivity"
                    />
                  </div>
                </div>

                {/* PII flag */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Contains PII</span>
                  <button
                    onClick={handleTogglePii}
                    className={`relative w-10 h-5 rounded-full transition-colors ${doc.classification?.containsPii ? "bg-emerald-500" : "bg-slate-300"}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${doc.classification?.containsPii ? "translate-x-5" : ""}`}
                    />
                  </button>
                </div>
              </div>
            </section>

            {/* Security Section */}
            <section className="bg-slate-50 rounded-xl p-4 mb-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Security
              </h3>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Visibility</span>
                <span className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 capitalize">
                  {doc.security?.visibility || "matter"}
                </span>
              </div>
            </section>

            {/* Legal Hold */}
            <section className="bg-slate-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${doc.retention?.legalHold ? "text-red-500" : "text-slate-400"}`} />
                  <span className="text-sm font-semibold text-slate-700">Legal Hold</span>
                </div>
                <button
                  onClick={handleToggleLegalHold}
                  className={`relative w-10 h-5 rounded-full transition-colors ${doc.retention?.legalHold ? "bg-red-500" : "bg-slate-300"}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${doc.retention?.legalHold ? "translate-x-5" : ""}`}
                  />
                </button>
              </div>
              {doc.retention?.legalHold && (
                <p className="text-xs text-red-600 mt-2">
                  This document is under legal hold and cannot be deleted.
                </p>
              )}
            </section>

            {/* Version History */}
            <section>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Version History ({doc.versions?.length || 0})
              </h3>
              <div className="space-y-2">
                {(doc.versions || []).map((version, index) => (
                  <div
                    key={version._id}
                    className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-slate-100 rounded">
                        <FileText className="h-4 w-4 text-slate-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium" style={{ color: "#1F2937" }}>
                            v{(doc.versions?.length || 0) - index}
                          </span>
                          {index === 0 && (
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {version.file.filename} -- {formatBytes(version.file.sizeBytes)}
                        </p>
                        <p className="text-xs text-slate-400">
                          {formatDate(version.createdAt)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(version)}
                      className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
                      title="Download this version"
                    >
                      <Download className="h-4 w-4 text-slate-500" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </motion.div>

      {/* AI Summary Panel */}
      <AnimatePresence>
        {showAiSummary && (
          <AiDocumentSummary
            documentId={documentId}
            documentTitle={doc?.title}
            onClose={() => setShowAiSummary(false)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DocumentDetail;
